// @flow
import React, { Component, Fragment } from "react";
import withStyles from "elevate-ui/withStyles";
import { withGlobalContext } from "../common/GlobalContext";
import enhanceWithClickOutisde from "react-click-outside";
import Color from "color";
import classNames from "classnames";

import ArrowDropDown from "elevate-ui-icons/ArrowDropDown";

import type { $SiteUser } from "flow/types";

type $Props = {
  classes: Object,
  siteUser: $SiteUser,
};

type $State = {
  dropdownOpen: boolean,
};

type $Dropdown = {
  classes: Object,
  isOpen: boolean,
  siteUser: $SiteUser,
};

const Dropdown = ({ classes, isOpen, siteUser }: $Dropdown) => {
  if (!isOpen) {
    return null;
  }

  return (
    <div className={classes.dropdown}>
      {!siteUser ? (
        <Fragment>
          <span>Search Like An Agent!</span>
          <a href="/signup" className={classes.button} title="Sign Up">
            Sign Up
          </a>
          <span>Already Signed Up?</span>
          <a href="/login" className={classes.button} title="Sign Up">
            Sign In
          </a>
        </Fragment>
      ) : (
        <ul className={classes.dropdownList}>
          <li>
            <a href="/favorites" title="Favorites">
              Favorites
            </a>
          </li>
          <li>
            <a href="/saved-searches" title="Saved Searches">
              Saved Searches
            </a>
          </li>
          <li>
            <a href="/account" title="Account">
              Account
            </a>
          </li>
          <li>
            <a href="/logout" title="Logout">
              Logout
            </a>
          </li>
        </ul>
      )}
    </div>
  );
};

class SignInSignUp extends Component<$Props, $State> {
  constructor(props) {
    super(props);

    this.state = {
      dropdownOpen: false,
    };
  }

  toggleDropdown = () => {
    this.setState((state) => ({ dropdownOpen: !state.dropdownOpen }));
  };

  handleClickOutside() {
    if (!this.state.dropdownOpen) {
      return null;
    }

    this.setState({ dropdownOpen: false });
  }

  render() {
    const { classes, siteUser } = this.props;
    const { dropdownOpen } = this.state;

    return (
      <li className={classes.root}>
        <button
          onClick={this.toggleDropdown}
          type="button"
          className={classNames({
            [classes.button]: true,
            [classes.buttonLoggedIn]: siteUser,
          })}
        >
          {!siteUser
            ? "Sign In | Sign Up"
            : `${siteUser && siteUser.firstName} `}
          {siteUser && <ArrowDropDown />}
        </button>
        <Dropdown siteUser={siteUser} classes={classes} isOpen={dropdownOpen} />
      </li>
    );
  }
}

const styles = (theme) => {
  const hoverColor = Color(theme.colors.primary["500"])
    .darken(0.5)
    .string();
  return {
    root: {
      color: theme.colors.primaryFontColor,
      "@media screen and (max-width: 767px)": {
        // we only want this to apply for mobile only then let button styles takeover from there
        "&:hover > button": {
          backgroundColor: hoverColor,
        },
      },
    },
    dropdown: {
      color: theme.colors.primaryFontColor,
      position: "static",
      float: "none",
      width: "100%",
      marginTop: "0",
      backgroundColor: "transparent",
      border: "0",
      display: "block",
      right: "-30px",
      top: "90%",
      zIndex: theme.zIndex.tooltip,
      minWidth: "160px",
      padding: "0 0 5px",
      margin: "2px 0 0",
      fontSize: "14px",
      textAlign: "center",
      listStyle: "none",
      [theme.breakpoints(900)]: {
        position: "absolute",
        backgroundColor: theme.colors.primary["500"],
        float: "right",
        border: "1px solid rgba(0,0,0,.15)",
        borderRadius: theme.globalBorderRadius,
        boxShadow: "0 6px 12px rgba(0,0,0,.175)",
      },
      "& > span": {
        display: "none",
        [theme.breakpoints(900)]: {
          display: "block",
          marginTop: theme.spacing.unit,
        },
      },
      "& $button": {
        width: "60%",
        "&:hover": {
          backgroundColor: hoverColor,
          borderColor: hoverColor,
          [theme.breakpoints(900)]: {
            backgroundColor: theme.colors.green["500"],
            borderColor: theme.colors.green["500"],
          },
        },
      },
    },
    buttonLoggedIn: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      [theme.breakpoints(900)]: {
        justifyContent: "space-around",
      },
    },
    dropdownList: {
      color: theme.colors.primaryFontColor,
      display: "block",
      position: "relative",
      width: "100%",
      padding: "0",
      fontSize: "14px",
      textAlign: "left",
      listStyle: "none",
      [theme.breakpoints(900)]: {
        float: "right",
      },
      "& li a": {
        color: theme.colors.primaryFontColor,
        padding: "8px 16px",
        margin: "6px auto",
        lineHeight: "20px",
        display: "block",
        position: "relative",
        fontSize: "1em",
        "&:hover, &:focus": {
          textDecoration: "none",
          backgroundColor: hoverColor,
        },
      },
    },
    button: {
      appearance: "none",
      "-webkit-appearance": "none",
      width: "100%",
      margin: "4px auto 0",
      padding: "12px 14px",
      fontSize: "16px",
      border: "none",
      backgroundColor: "transparent",
      color: "inherit",
      transition: "ease .3s all",
      [theme.breakpoints(900)]: {
        padding: "5px",
        fontSize: "14px",
        lineHeight: "1.5",
        borderRadius: "3px",
        color: theme.colors.white,
        backgroundColor: theme.colors.green["600"],
        display: "inline-block",
        margin: "11px 0px 11px 14px",
        fontWeight: "normal",
        textAlign: "center",
        verticalAlign: "middle",
        touchAction: "manipulation",
        cursor: "pointer",
        backgroundImage: "none",
        border: "1px solid transparent",
        whiteSpace: "nowrap",
        "&:hover, &:focus": {
          backgroundColor: theme.colors.green["500"],
          borderColor: theme.colors.green["500"],
        },
        [theme.breakpoints(1120)]: {
          margin: "22px 0px 22px 28px",
        },
      },
    },
  };
};

export default withGlobalContext(
  withStyles(styles)(enhanceWithClickOutisde(SignInSignUp))
);
